import {
  DNA_LIST_RESOURCES,
  canonicalDnaResource,
  dnaTableNameFor,
} from "@Simplicity-Education/core";
import {
  Alert,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import type { TableConfig } from "./types";

interface DnAFieldProps {
  table: TableConfig;
  update: (patch: Partial<TableConfig>) => void;
}

const KNOWN_RESOURCES: ReadonlySet<string> = new Set(DNA_LIST_RESOURCES);

function resourceOf(table: TableConfig): string | undefined {
  return typeof table.resource === "string" && table.resource !== "" ? table.resource : undefined;
}

// Per-ID resources carry a `fanOut` config this editor can't express, so they stay read-only.
function isFanOut(resource: string | undefined): resource is string {
  return resource?.includes("{id}") ?? false;
}

function isUnrecognised(resource: string | undefined): resource is string {
  return resource !== undefined && !isFanOut(resource) && !KNOWN_RESOURCES.has(resource);
}

// A name that was only ever derived from the old endpoint follows the new one; a hand-typed name is kept.
function nameFollowsEndpoint(name: string, previous: string | undefined): boolean {
  if (name === "") return true;
  if (previous === undefined) return false;
  return name === dnaTableNameFor(previous) || name === previous.toLowerCase();
}

function patchForEndpoint(table: TableConfig, chosen: string): Partial<TableConfig> {
  return nameFollowsEndpoint(table.name, resourceOf(table))
    ? { resource: chosen, name: dnaTableNameFor(chosen) }
    : { resource: chosen };
}

/** The "API Endpoint" control for a DnA table — the case-sensitive path the runner requests. */
export function DnAEndpointField({ table, index, update }: DnAFieldProps & { index: number }) {
  const resource = resourceOf(table);
  const id = `table-resource-${index}`;

  if (isFanOut(resource)) {
    return (
      <div className="flex-1">
        <Label htmlFor={id} className="form-label">
          API Endpoint
        </Label>
        <Input id={id} type="text" value={resource} disabled readOnly className="font-mono" />
      </div>
    );
  }

  return (
    <div className="flex-1">
      <Label htmlFor={id} className="form-label">
        API Endpoint
      </Label>
      <Select
        value={resource ?? ""}
        onValueChange={(chosen) => update(patchForEndpoint(table, chosen))}
      >
        <SelectTrigger id={id} className="font-mono">
          <SelectValue placeholder="Choose an endpoint" />
        </SelectTrigger>
        <SelectContent>
          {isUnrecognised(resource) && (
            <SelectItem value={resource}>{`${resource} (unrecognised)`}</SelectItem>
          )}
          {DNA_LIST_RESOURCES.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Guidance shown beneath a DnA table's top row: missing, mis-cased, or per-ID endpoints. */
export function DnAEndpointNotice({ table, update }: DnAFieldProps) {
  const resource = resourceOf(table);

  if (resource === undefined) {
    return (
      <p className="text-xs text-muted-foreground">
        Without an endpoint the connector requests{" "}
        <code className="font-mono">/Api/{table.name || "<name>"}/</code>, and DnA endpoints are
        case-sensitive.
      </p>
    );
  }

  if (isFanOut(resource)) {
    return (
      <p className="text-xs text-muted-foreground">Per-ID fan-out — edit in the config JSON.</p>
    );
  }

  if (!isUnrecognised(resource)) return null;

  const match = canonicalDnaResource(resource);
  return (
    <Alert variant="warning" className="flex items-center justify-between gap-4">
      <p>
        DnA endpoints are case-sensitive — <code className="font-mono">{resource}</code> will 404.
      </p>
      {match && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => update({ resource: match })}
        >
          Use {match}
        </Button>
      )}
    </Alert>
  );
}
